import React, { useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import Icon from "@identitybuilding/idb-react-iconlib";
import { getEnvEndpoints } from "../../utils/functions";

const PasswordChangeForm = ({ createNotification }) => {
    const { translate } = useSelector((state) => state.general);
    const env = getEnvEndpoints();

    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [viewCurrentPassword, setViewCurrentPassword] = useState(false);
    const [viewNewPassword, setViewNewPassword] = useState(false);
    const [viewConfirmPassword, setViewConfirmPassword] = useState(false);

    const isUpper = (str) => /[A-Z]/.test(str);
    const hasNumber = (str) => /[0-9]/.test(str);

    const validatePassword = () => {
        if (!currentPassword) {
            createNotification("warning", translate("enter_current_password") || "Voer uw huidige wachtwoord in");
            return false;
        }
        if (newPassword.length < 8) {
            createNotification("warning", translate("password_8_characters") || "Wachtwoord moet minimaal 8 tekens bevatten");
            return false;
        }
        if (!isUpper(newPassword)) {
            createNotification("warning", translate("password_1_capital") || "Wachtwoord moet minimaal 1 hoofdletter bevatten");
            return false;
        }
        if (!hasNumber(newPassword)) {
            createNotification("warning", translate("password_1_number") || "Wachtwoord moet minimaal 1 cijfer bevatten");
            return false;
        }
        if (newPassword !== confirmPassword) {
            createNotification("warning", translate("passwords_do_not_match") || "Wachtwoorden komen niet overeen");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validatePassword()) return;

        setLoading(true);
        try {
            const headers = {
                "Content-Type": "application/json",
                Authorization: `Token ${localStorage.getItem("StadGemeenteToken")}`,
            };

            await axios.post(
                `${env.REACT_APP_BASE_MEDIA_URL}/core/api/stadgemeente/change-password/`,
                {
                    current_password: currentPassword,
                    new_password: newPassword,
                },
                { headers }
            );

            createNotification("success", translate("password_changed") || "Wachtwoord succesvol gewijzigd");
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
        } catch (error) {
            console.error("Password change error:", error);
            if (error.response?.data?.status === "Current password is incorrect") {
                createNotification("error", translate("current_password_incorrect") || "Huidig wachtwoord is onjuist");
            } else {
                createNotification("error", translate("password_change_failed") || "Wachtwoord wijzigen mislukt");
            }
        }
        setLoading(false);
    };

    return (
        <section className="settings-section">
            <h2>{translate("change_password") || "Wachtwoord wijzigen"}</h2>
            <p className="section-description">{translate("password_section_description") || "Kies een sterk wachtwoord om uw account te beveiligen."}</p>
            <form onSubmit={handleSubmit} className="password-form">
                {/* Current Password */}
                <div className="float-input-field">
                    <div className="input-group">
                        <input
                            id="current_password"
                            name="current_password"
                            type={viewCurrentPassword ? "text" : "password"}
                            placeholder={translate("current_password") || "Huidig wachtwoord"}
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            className="form-field"
                            autoComplete="off"
                        />
                        <label htmlFor="current_password" className="input-label">
                            {translate("current_password") || "Huidig wachtwoord"}
                        </label>
                        <div
                            className="password-toggle-icon"
                            onClick={() => setViewCurrentPassword(!viewCurrentPassword)}
                            title={viewCurrentPassword ? (translate("hide_password") || "Verberg wachtwoord") : (translate("show_password") || "Toon wachtwoord")}
                        >
                            <Icon name={viewCurrentPassword ? "PasswordShow" : "PasswordHide"} />
                        </div>
                    </div>
                </div>

                {/* New Password */}
                <div className="float-input-field">
                    <div className="input-group">
                        <input
                            id="new_password"
                            name="new_password"
                            type={viewNewPassword ? "text" : "password"}
                            placeholder={translate("new_password") || "Nieuw wachtwoord"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="form-field"
                            autoComplete="off"
                        />
                        <label htmlFor="new_password" className="input-label">
                            {translate("new_password") || "Nieuw wachtwoord"}
                        </label>
                        <div
                            className="password-toggle-icon"
                            onClick={() => setViewNewPassword(!viewNewPassword)}
                            title={viewNewPassword ? (translate("hide_password") || "Verberg wachtwoord") : (translate("show_password") || "Toon wachtwoord")}
                        >
                            <Icon name={viewNewPassword ? "PasswordShow" : "PasswordHide"} />
                        </div>
                    </div>
                </div>

                {/* Confirm Password */}
                <div className="float-input-field">
                    <div className="input-group">
                        <input
                            id="confirm_password"
                            name="confirm_password"
                            type={viewConfirmPassword ? "text" : "password"}
                            placeholder={translate("confirm_password") || "Bevestig wachtwoord"}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="form-field"
                            autoComplete="off"
                        />
                        <label htmlFor="confirm_password" className="input-label">
                            {translate("confirm_password") || "Bevestig wachtwoord"}
                        </label>
                        <div
                            className="password-toggle-icon"
                            onClick={() => setViewConfirmPassword(!viewConfirmPassword)}
                            title={viewConfirmPassword ? (translate("hide_password") || "Verberg wachtwoord") : (translate("show_password") || "Toon wachtwoord")}
                        >
                            <Icon name={viewConfirmPassword ? "PasswordShow" : "PasswordHide"} />
                        </div>
                    </div>
                </div>

                {/* Password Requirements */}
                <div className="password-requirements">
                    <p>{translate("password_requirements") || "Wachtwoord vereisten:"}</p>
                    <ul>
                        <li className={newPassword.length >= 8 ? "valid" : ""}>
                            {translate("password_8_characters") || "Minimaal 8 tekens"}
                        </li>
                        <li className={isUpper(newPassword) ? "valid" : ""}>
                            {translate("password_1_capital") || "Minimaal 1 hoofdletter"}
                        </li>
                        <li className={hasNumber(newPassword) ? "valid" : ""}>
                            {translate("password_1_number") || "Minimaal 1 cijfer"}
                        </li>
                    </ul>
                </div>

                {/* Submit Button */}
                <div className="form-button">
                    <button type="submit" className="submit-button" disabled={loading}>
                        {loading ? (translate("updating") || "Bijwerken...") : (translate("change_password") || "Wachtwoord wijzigen")}
                    </button>
                </div>
            </form>
        </section>
    );
};

export default PasswordChangeForm;
